import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { authApi, setCurrentUser, subscribeCurrentUser, auth } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Facilities from "@/pages/Facilities";
import Watchlist from "@/pages/Watchlist";
import AuditLog from "@/pages/AuditLog";
import Users from "@/pages/Users";
import Import from "@/pages/Import";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import AiEdit from "@/pages/AiEdit";
import Plans from "@/pages/Plans";
import Companies from "@/pages/Companies";
import SentryTest from "@/pages/SentryTest";
import AccessDenied from "@/pages/AccessDenied";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [, force] = useState(0);

  useEffect(() => subscribeCurrentUser(() => force((n) => n + 1)), []);

  useEffect(() => {
    (async () => {
      try {
        const me = await authApi.me();
        setCurrentUser(me);
      } catch {
        setCurrentUser(null);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-slate-500" data-testid="auth-loading">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  return children;
}

const Protected = ({ children, permission = null }) => {
  const me = auth.me();
  const { has, ready } = usePermissions();
  if (!me) return <Navigate to="/login" replace />;
  if (permission && ready && !has(permission)) {
    return <AccessDenied permission={permission} />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/" element={<Protected><Facilities /></Protected>} />
            <Route path="/watchlist" element={<Protected><Watchlist /></Protected>} />
            <Route path="/audit" element={<Protected permission="hsp:audit:read"><AuditLog /></Protected>} />
            <Route path="/users" element={<Protected permission="hsp:users:read"><Users /></Protected>} />
            <Route path="/import" element={<Protected permission="hsp:import:facilities_upsert"><Import /></Protected>} />
            <Route path="/reports" element={<Protected permission="hsp:reports:read"><Reports /></Protected>} />
            <Route path="/notifications" element={<Protected permission="hsp:notifications:read"><Notifications /></Protected>} />
            <Route path="/ai-edit" element={<Protected permission="hsp:ai:consult"><AiEdit /></Protected>} />
            <Route path="/plans" element={<Protected permission="hsp:plans:read"><Plans /></Protected>} />
            <Route path="/companies" element={<Protected permission="hsp:companies:read"><Companies /></Protected>} />
            <Route path="/sentry-test" element={<Protected><SentryTest /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
